export enum QuoteStatus {
  DRAFT = "DRAFT",
  SENT = "SENT",
  ACCEPTED = "ACCEPTED",
}

export type QuoteStatusFilter = QuoteStatus | "ALL";

export interface Quote {
  id: string;
  quoteNumber: string;
  createdAt: string;
  customerNameKey: string;
  projectTypeKey: string;
  revisionNumber: number;
  totalValue: number;
  status: QuoteStatus;
  validUntil: string;
  descriptionKey: string;
}

export interface QuoteSelectOption {
  value: string;
  labelKey: string;
}

export enum QuoteCrossBeams {
  OMEGA_3MM = "OMEGA_3MM",
  OMEGA_2MM = "OMEGA_2MM",
  OMEGA_2MM_STR = "OMEGA_2MM_STR",
  OMEGA_3MM_STR = "OMEGA_3MM_STR",
  I100 = "I100",
  I80 = "I80",
  U80 = "U80",
  U100 = "U100",
  OMEGA_2_5MM_WOOD = "OMEGA_2_5MM_WOOD",
  OMEGA_3MM_WOOD = "OMEGA_3MM_WOOD",
  OMEGA_120X103_INSULATED = "OMEGA_120X103_INSULATED",
  OMEGA_120X83_INSULATED = "OMEGA_120X83_INSULATED",
  RECESSED_BOX = "RECESSED_BOX",
  ALUMINUM = "ALUMINUM",
}

const quoteCrossBeamsOption = (
  value: QuoteCrossBeams,
  name: string
): QuoteSelectOption => ({
  value,
  labelKey: `QUOTES.DETAILS.FORM.OPTIONS.CROSS_BEAMS_${name}`,
});

export const CROSS_BEAMS_OPTIONS: QuoteSelectOption[] = [
  quoteCrossBeamsOption(QuoteCrossBeams.OMEGA_3MM, "OMEGA_3MM"),
  quoteCrossBeamsOption(QuoteCrossBeams.OMEGA_2MM, "OMEGA_2MM"),
  quoteCrossBeamsOption(QuoteCrossBeams.OMEGA_2MM_STR, "OMEGA_2MM_STR"),
  quoteCrossBeamsOption(QuoteCrossBeams.OMEGA_3MM_STR, "OMEGA_3MM_STR"),
  quoteCrossBeamsOption(QuoteCrossBeams.I100, "I100"),
  quoteCrossBeamsOption(QuoteCrossBeams.I80, "I80"),
  quoteCrossBeamsOption(QuoteCrossBeams.U80, "U80"),
  quoteCrossBeamsOption(QuoteCrossBeams.U100, "U100"),
  quoteCrossBeamsOption(QuoteCrossBeams.OMEGA_2_5MM_WOOD, "OMEGA_2_5MM_WOOD"),
  quoteCrossBeamsOption(QuoteCrossBeams.OMEGA_3MM_WOOD, "OMEGA_3MM_WOOD"),
  quoteCrossBeamsOption(
    QuoteCrossBeams.OMEGA_120X103_INSULATED,
    "OMEGA_120X103_INSULATED"
  ),
  quoteCrossBeamsOption(
    QuoteCrossBeams.OMEGA_120X83_INSULATED,
    "OMEGA_120X83_INSULATED"
  ),
  quoteCrossBeamsOption(QuoteCrossBeams.RECESSED_BOX, "RECESSED_BOX"),
  quoteCrossBeamsOption(QuoteCrossBeams.ALUMINUM, "ALUMINUM"),
];

export enum QuoteStrapTensionerType {
  WIDE = "WIDE",
  NARROW = "NARROW",
}

export const STRAP_TENSIONER_TYPE_OPTIONS: QuoteSelectOption[] = [
  {
    value: QuoteStrapTensionerType.WIDE,
    labelKey: "QUOTES.DETAILS.FORM.TYING_EQUIPMENT.OPTIONS.WIDE",
  },
  {
    value: QuoteStrapTensionerType.NARROW,
    labelKey: "QUOTES.DETAILS.FORM.TYING_EQUIPMENT.OPTIONS.NARROW",
  },
];

export enum QuoteWingMaterial {
  TIN = "TIN",
  PLASTIC = "PLASTIC",
}

export const WING_MATERIAL_OPTIONS: QuoteSelectOption[] = [
  {
    value: QuoteWingMaterial.TIN,
    labelKey: "QUOTES.DETAILS.FORM.SAFETY_VEHICLE.OPTIONS.TIN",
  },
  {
    value: QuoteWingMaterial.PLASTIC,
    labelKey: "QUOTES.DETAILS.FORM.SAFETY_VEHICLE.OPTIONS.PLASTIC",
  },
];

export enum QuoteWingLocation {
  FRONT = "FRONT",
  REAR = "REAR",
  FRONT_AND_REAR = "FRONT_AND_REAR",
}

export const WING_LOCATION_OPTIONS: QuoteSelectOption[] = [
  {
    value: QuoteWingLocation.FRONT,
    labelKey: "QUOTES.DETAILS.FORM.SAFETY_VEHICLE.OPTIONS.FRONT",
  },
  {
    value: QuoteWingLocation.REAR,
    labelKey: "QUOTES.DETAILS.FORM.SAFETY_VEHICLE.OPTIONS.REAR",
  },
  {
    value: QuoteWingLocation.FRONT_AND_REAR,
    labelKey: "QUOTES.DETAILS.FORM.SAFETY_VEHICLE.OPTIONS.FRONT_AND_REAR",
  },
];

export interface QuoteDetailsSummary {
  workOrderNo: string;
  customerNameKey: string;
  subCustomerKey: string;
  chassis: string;
  manufacturerKey: string;
  model: string;
  axleConfiguration: string;
  year: string;
  boxTypeKey: string;
  totalSale: number;
  theoreticalPrice: number;
  totalCost: number;
  totalMaterialCost: number;
  profitAmount: number;
  profitPercent: number;
}

export interface QuoteOptionItem {
  enabled: boolean;
  selectedOption: string;
  note: string;
  quantity: string;
  size: string;
  location: string;
  itemType: string;
  optionEnabled: boolean;
}

export interface QuoteAccessoryTableRow {
  id: string;
  itemType: string;
  quantity: string;
  note: string;
  height: string;
}

export type QuoteAccessoryTableKey = "toolboxes" | "openBasket";

export type QuoteTyingEquipmentCard =
  | "sideCableTensioner"
  | "strapTensioners"
  | "frontCableTensioner"
  | "recessedRings"
  | "apcRings"
  | "additionalTyingHooks";

export type QuoteStorageAccessoriesCard =
  | "polesBasket"
  | "waterJerrycan"
  | "palletJackMount";

export type QuoteSafetyAndStandardsCard =
  | "rearBumper"
  | "hazardousMaterialsMounts"
  | "spareWheelCarrier"
  | "rearLightMounts"
  | "extinguisherMounts"
  | "couplingLocks";

export type QuoteVehicleAddonsCard = "wings" | "roundIronSides";

export type QuoteLocksCard =
  | "dieselLock"
  | "ureaLock"
  | "batteriesLock"
  | "hydraulicTailLiftMotorLock"
  | "hydraulicTailLiftLock"
  | "spareWheelLock";

export type QuoteStandardCard =
  | "sideGuardsReflectors"
  | "sideLighting"
  | "standardMudguards";

export interface QuoteAccessories {
  tyingEquipment: Record<QuoteTyingEquipmentCard, QuoteOptionItem>;
  storageAccessories: Record<QuoteStorageAccessoriesCard, QuoteOptionItem>;
  tables: Record<QuoteAccessoryTableKey, QuoteAccessoryTableRow[]>;
  safetyAndStandards: Record<QuoteSafetyAndStandardsCard, QuoteOptionItem>;
  vehicleAddons: Record<QuoteVehicleAddonsCard, QuoteOptionItem>;
  locks: Record<QuoteLocksCard, QuoteOptionItem>;
  standard: Record<QuoteStandardCard, QuoteOptionItem>;
}

export type QuoteAccessoriesCardSection = Exclude<
  keyof QuoteAccessories,
  "tables"
>;

export type QuoteAccessoriesUpdate =
  | {
      section: "tyingEquipment";
      key: QuoteTyingEquipmentCard;
      value: Partial<QuoteOptionItem>;
    }
  | {
      section: "storageAccessories";
      key: QuoteStorageAccessoriesCard;
      value: Partial<QuoteOptionItem>;
    }
  | {
      section: "safetyAndStandards";
      key: QuoteSafetyAndStandardsCard;
      value: Partial<QuoteOptionItem>;
    }
  | {
      section: "vehicleAddons";
      key: QuoteVehicleAddonsCard;
      value: Partial<QuoteOptionItem>;
    }
  | {
      section: "locks";
      key: QuoteLocksCard;
      value: Partial<QuoteOptionItem>;
    }
  | {
      section: "standard";
      key: QuoteStandardCard;
      value: Partial<QuoteOptionItem>;
    };

export interface QuoteFrontWall {
  enabled: boolean;
  selectedOption: string;
  height: string;
  treadSurface: boolean;
  treadNote: string;
  widthReinforcement: boolean;
  widthNote: string;
}

export interface QuoteBoxStructure {
  frontWall: QuoteFrontWall;
  backWall: QuoteOptionItem;
  frontRack: QuoteOptionItem;
  backRack: QuoteOptionItem;
  sideDoors: QuoteOptionItem;
  pillars: QuoteOptionItem;
  hydraulicSide: QuoteOptionItem;
  rearDoor: QuoteOptionItem;
}

export type QuoteDoorCard =
  | "pillars"
  | "sideDoors"
  | "rearDoor"
  | "hydraulicSide";

export type QuoteBoxStructurePatch = (
  key: keyof QuoteBoxStructure,
  value: Partial<QuoteFrontWall> | Partial<QuoteOptionItem>
) => void;

export interface QuoteQuotationForm {
  workHours: string;
  perHour: string;
  totalWorkCost: string;
  repairHours: string;
  sheetMetalCost: string;
  craneCompletionAddition: string;
  exchangeRate: string;
  otherSaleItem: string;
  variableCoefficient: string;
  downPayment: string;
  vehicleWeightWithoutSide: string;
  assembledLength: string;
  assembledWidth: string;
  floorBase: string;
  floorType: string;
  floorThickness: string;
  crossBeams: string;
  reinforcedModel: boolean;
}

export interface QuoteForm {
  vehicleImage: string;
  vehicleImageName: string;
  customerName: string;
  subCustomer: string;
  chassis: string;
  manufacturer: string;
  model: string;
  axleConfiguration: string;
  year: string;
  boxType: string;
}

export interface QuotesState {
  list: Quote[];
  selectedQuote: Quote | null;
  quoteDetails: QuoteDetailsSummary | null;
  quotationForm: QuoteQuotationForm | null;
  boxStructure: QuoteBoxStructure | null;
  accessories: QuoteAccessories | null;
  loading: boolean;
  detailLoading: boolean;
  error: string | null;
  search: string;
  statusFilter: QuoteStatusFilter[];
}